import { computed, h } from 'vue';
import { ClipboardIcon } from '@hugeicons/core-free-icons';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

const settingsIcon = icon =>
  h(HugeIcon, {
    icon,
    size: 20,
    class: 'shrink-0',
  });

/**
 * Shared settings sidebar tree for main Sidebar + SettingsWrapper inner nav.
 * Account, billing, and profile are excluded (main rail + profile menu).
 */
export function useSettingsNavConfig() {
  const { t } = useI18n();
  const { accountScopedRoute } = useAccount();
  const accountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );
  const hasAdvancedAssignment = computed(() =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.ADVANCED_ASSIGNMENT
    )
  );

  const assignmentPolicyLeaves = computed(() =>
    hasAdvancedAssignment.value
      ? [
          {
            name: 'Workspace Agent Assignment',
            label: t('SIDEBAR.AGENT_ASSIGNMENT'),
            icon: 'i-lucide-user-cog',
            activeOn: [
              'assignment_policy_index',
              'agent_assignment_policy_index',
              'agent_assignment_policy_create',
              'agent_assignment_policy_edit',
              'agent_capacity_policy_index',
              'agent_capacity_policy_create',
              'agent_capacity_policy_edit',
            ],
            to: accountScopedRoute('assignment_policy_index'),
          },
        ]
      : []
  );

  const generalInfoNavSection = computed(() => ({
    name: 'Settings section general info',
    label: t('SIDEBAR.SETTINGS_NAV.GENERAL_INFO'),
    icon: 'i-lucide-info',
    children: [
      {
        name: 'Settings General Info',
        label: t('SIDEBAR.SETTINGS_NAV.GENERAL_INFO'),
        icon: 'i-lucide-info',
        to: accountScopedRoute('general_info_index'),
      },
    ],
  }));

  const securityNavSection = computed(() => ({
    name: 'Settings section security',
    label: t('SIDEBAR.SECURITY'),
    icon: 'i-lucide-shield',
    children: [
      {
        name: 'Settings Security',
        label: t('SIDEBAR.SECURITY'),
        icon: 'i-lucide-shield',
        to: accountScopedRoute('security_settings_index'),
      },
    ],
  }));

  const userRoleSettingsSection = computed(() => ({
    name: 'Settings section user role',
    label: t('SIDEBAR.SETTINGS_NAV.USER_ROLE_SECTION'),
    icon: 'i-lucide-users-round',
    children: [
      {
        name: 'Settings Users',
        label: t('SIDEBAR.SETTINGS_NAV.USERS_SETTINGS'),
        icon: 'i-lucide-square-user',
        to: accountScopedRoute('agent_list'),
      },
      {
        name: 'Settings Teams',
        label: t('SIDEBAR.SETTINGS_NAV.TEAM_SETTINGS'),
        icon: 'i-lucide-users',
        activeOn: [
          'settings_teams_list',
          'settings_teams_new',
          'settings_teams_finish',
          'settings_teams_add_agents',
          'settings_teams_show',
          'settings_teams_edit',
          'settings_teams_edit_members',
          'settings_teams_edit_finish',
        ],
        to: accountScopedRoute('settings_teams_list'),
      },
      ...assignmentPolicyLeaves.value,
    ],
  }));

  const appsSection = computed(() => ({
    name: 'Settings section apps',
    label: t('SIDEBAR.SETTINGS_NAV.APPS_SECTION'),
    icon: 'i-lucide-layout-grid',
    children: [
      {
        name: 'Settings Inboxes',
        label: t('SIDEBAR.INBOXES'),
        icon: settingsIcon(ClipboardIcon),
        activeOn: [
          'settings_inbox_list',
          'settings_inbox_show',
          'settings_inbox_new',
          'settings_inbox_finish',
          'settings_inboxes_page_channel',
        ],
        to: accountScopedRoute('settings_inbox_list'),
      },
      {
        name: 'Settings Integrations',
        label: t('SIDEBAR.INTEGRATIONS'),
        icon: 'i-lucide-blocks',
        to: accountScopedRoute('settings_applications'),
      },
    ],
  }));

  const inboxSettingsSection = computed(() => ({
    name: 'Settings section inbox settings',
    label: t('SIDEBAR.SETTINGS_NAV.INBOX_SETTINGS_SECTION'),
    icon: 'i-lucide-mailbox',
    children: [
      {
        name: 'Settings Contact Fields',
        label: t('SIDEBAR.CONTACT_FIELDS'),
        icon: 'i-lucide-contact',
        activeOn: ['attributes_list', 'contact_fields_list'],
        to: accountScopedRoute('contact_fields_list'),
      },
      {
        name: 'Settings Lifecycle',
        label: t('SIDEBAR.LIFECYCLE'),
        icon: 'i-lucide-refresh-cw',
        to: accountScopedRoute('lifecycle_stages_list'),
      },
      {
        name: 'Settings Business Hours',
        label: t('SIDEBAR.SETTINGS_NAV.BUSINESS_HOURS'),
        icon: 'i-lucide-clock-3',
        activeOn: ['business_hours_settings'],
        to: accountScopedRoute('business_hours_settings'),
      },
      {
        name: 'Settings Closing Note',
        label: t('SIDEBAR.SETTINGS_NAV.CLOSING_NOTE'),
        icon: 'i-lucide-file-text',
        activeOn: ['conversation_workflow_index'],
        to: accountScopedRoute('conversation_workflow_index'),
      },
      {
        name: 'Settings Labels',
        label: t('SIDEBAR.LABELS'),
        icon: 'i-lucide-tags',
        to: accountScopedRoute('labels_list'),
      },
      {
        name: 'Settings Canned Responses',
        label: t('SIDEBAR.CANNED_RESPONSES'),
        icon: 'i-lucide-message-square-quote',
        to: accountScopedRoute('canned_list'),
      },
      {
        name: 'Settings AI Assistant',
        label: t('SIDEBAR.SETTINGS_NAV.AI_ASSISTANT'),
        icon: 'i-lucide-bot',
        activeOn: ['captain_settings_index'],
        to: accountScopedRoute('captain_settings_index'),
      },
      {
        name: 'Settings AI Prompt',
        label: t('SIDEBAR.SETTINGS_NAV.AI_PROMPT'),
        icon: 'i-lucide-sparkles',
        activeOn: [
          'captain_assistants_guidelines_index',
          'captain_assistants_guardrails_index',
        ],
        to: accountScopedRoute('captain_assistants_index', {
          navigationPath: 'captain_assistants_guidelines_index',
        }),
      },
      {
        name: 'Settings AI Assist',
        label: t('SIDEBAR.SETTINGS_NAV.AI_ASSIST'),
        icon: 'i-lucide-message-circle',
        activeOn: ['ai_assist_settings'],
        to: accountScopedRoute('ai_assist_settings'),
      },
      {
        name: 'Settings AI Knowledge Sources',
        label: t('SIDEBAR.SETTINGS_NAV.AI_KNOWLEDGE_SOURCES'),
        icon: 'i-lucide-book-open',
        activeOn: ['ai_knowledge_sources_settings'],
        to: accountScopedRoute('ai_knowledge_sources_settings'),
      },
      {
        name: 'Settings AI Prompts',
        label: t('SIDEBAR.SETTINGS_NAV.AI_PROMPTS'),
        icon: 'i-lucide-zap',
        activeOn: ['ai_prompts_settings'],
        to: accountScopedRoute('ai_prompts_settings'),
      },
    ],
  }));

  const otherSettingsChildren = computed(() => {
    const children = [];

    return children;
  });

  const otherSettingsSection = computed(() => ({
    name: 'Settings section other',
    label: t('SIDEBAR.SETTINGS_NAV.OTHER_SECTION'),
    icon: 'i-lucide-ellipsis',
    children: otherSettingsChildren.value,
  }));

  const settingsNavChildren = computed(() => [
    generalInfoNavSection.value,
    securityNavSection.value,
    userRoleSettingsSection.value,
    appsSection.value,
    inboxSettingsSection.value,
    ...(otherSettingsSection.value.children.length
      ? [otherSettingsSection.value]
      : []),
  ]);

  return {
    settingsNavChildren,
  };
}
